import { ChangeEvent, ReactNode, useState } from 'react';
import {
  getValidationError,
  ValidationRule,
} from '../../utility/validation/validation';
import _, { trim } from 'lodash';
import { HttpError } from '../../config/Axios/axios-instance';
import { Asset } from '../../domain/Asset';
import { useIntl } from 'react-intl';
import { EntityTranslation } from '../../domain/EntityTranslation';
import { SignUpSectionType } from '../../pages/Public/SignUpPage/SignUpPage';
import { RadioProps } from '../../common/TextField/TextField';

const IGNORED_TRIM = [
  'password',
  'file',
  'profileImage',
  'autocomplete',
  'custom',
  'upload',
  'translations',
  'dynamic',
];

export type LabelPlacement = 'end' | 'start' | 'top' | 'bottom';

export type FormInputBlueprint = {
  name: string;
  label?: ReactNode;
  type: string;
  validation?: Array<ValidationRule>;
  value?: string | string[] | number | File | File[] | null | any[];
  isHidden?: boolean;
  options?: Array<any>;
  placeholder?: string;
  helperText?: string;
  disabled?: boolean;
  capitalize?: boolean;
  locale?: string;
  disableClearable?: boolean;
  multiple?: boolean;
  labelPlacement?: LabelPlacement;
  alternativeNames?: string[];
  asset?: Asset | null;
  creatable?: boolean;
  maxFileCount?: number;
  freeSolo?: boolean;
  onFetch?: (search: string) => void;
  onCreate?: () => void;
  maxLimit?: number;
  forgotPassword?: {
    label: string;
    section: SignUpSectionType;
  };
  radioProps?: RadioProps;
  areTranslationsOptional?: boolean;
  className?: string;
  required?: boolean;
  checkboxContainer?: ReactNode;
  acceptedFileTypes?: string[];
  fileInfo?: string;
  dynamicFieldConfig?: FormInput[];
  dynamicValues?: Record<string, any>[];
  onDynamicChange?: (values: Record<string, any>[]) => void;
};

export type FormInput = FormInputBlueprint & {
  isValidatable?: boolean;
  validationErrors?: Array<string>;
  dynamicInputValidationErrors?: Record<string, string[]>[];
};

export type FormBehavior = {
  submitOnChange?: boolean;
  submitOnBlur?: boolean;
};

export type FormSubmitInput = {
  [key: string]: string;
};

export const useForm = <T>(
  inputBlueprints: Array<FormInputBlueprint>,
  onFormSubmit?: (inputs: T) => void,
  formBehavior?: FormBehavior,
) => {
  const intl = useIntl();

  const [inputs, setInputs] = useState<Array<FormInput>>(inputBlueprints);

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    setInputs((prevState) => {
      const inputs = prevState.map((prevInput) => {
        return prevInput.name === event.target.name
          ? {
              ...prevInput,
              value: prevInput.capitalize
                ? event.target.value.toUpperCase()
                : event.target.value,
              validationErrors: prevInput.isValidatable
                ? getValidationError(
                    event.target.value,
                    prevInput.validation,
                    intl,
                  )
                : [],
            }
          : { ...prevInput };
      });

      formBehavior?.submitOnChange &&
        onFormSubmit &&
        onFormSubmit(getSubmitInputs(inputs));

      return inputs;
    });
  };

  const onNumberInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    setInputs((prevState) => {
      const inputs = prevState.map((prevInput) => {
        return prevInput.name === event.target.name
          ? {
              ...prevInput,
              value: prevInput.capitalize
                ? event.target.value.toUpperCase().replace(/[^0-9]/g, '')
                : event.target.value.replace(/[^0-9]/g, ''),
              validationErrors: prevInput.isValidatable
                ? getValidationError(
                    event.target.value,
                    prevInput.validation,
                    intl,
                  )
                : [],
            }
          : { ...prevInput };
      });

      formBehavior?.submitOnChange &&
        onFormSubmit &&
        onFormSubmit(getSubmitInputs(inputs));

      return inputs;
    });
  };

  const onCheckboxChange = (name: string, value: string) => {
    setInputs((prevState) => {
      const inputs = prevState.map((prevInput) =>
        prevInput.name === name
          ? {
              ...prevInput,
              value: value,
              validationErrors: prevInput.isValidatable
                ? getValidationError(value, prevInput.validation, intl)
                : [],
            }
          : { ...prevInput },
      );

      formBehavior?.submitOnChange &&
        onFormSubmit &&
        onFormSubmit(getSubmitInputs(inputs));

      return inputs;
    });
  };

  const onRadioChange = (name: string, value: string) => {
    setInputs((prevState) =>
      prevState.map((prevInput) =>
        prevInput.name === name
          ? {
              ...prevInput,
              value: value,
              validationErrors: prevInput.isValidatable
                ? getValidationError(value, prevInput.validation, intl)
                : [],
            }
          : { ...prevInput },
      ),
    );

    formBehavior?.submitOnChange &&
      onFormSubmit &&
      setTimeout(() => {
        onFormSubmit(getSubmitInputs(inputs));
      }, 10);
  };

  const onInputValueChange = (name: string, value: string) => {
    setInputs((prevState) => {
      const inputs = prevState.map((prevInput) =>
        prevInput.name === name
          ? {
              ...prevInput,
              value: value,
              validationErrors: prevInput.isValidatable
                ? getValidationError(value, prevInput.validation, intl)
                : [],
            }
          : { ...prevInput },
      );

      formBehavior?.submitOnChange &&
        onFormSubmit &&
        onFormSubmit(getSubmitInputs(inputs));

      return inputs;
    });
  };

  const onTranslationsChange = (name: string, value: EntityTranslation[]) => {
    setInputs((prevState) =>
      prevState.map((prevInput) =>
        prevInput.name === name
          ? {
              ...prevInput,
              value: value,
              validationErrors: onTranslationValidation(prevInput),
            }
          : { ...prevInput },
      ),
    );
  };

  const onTimeChange = (name: string, value: string) => {
    setInputs((prevState) => {
      const inputs = prevState.map((prevInput) =>
        prevInput.name === name
          ? {
              ...prevInput,
              value: value,
              validationErrors: prevInput.isValidatable
                ? getValidationError(value, prevInput.validation, intl)
                : [],
            }
          : { ...prevInput },
      );

      formBehavior?.submitOnChange &&
        onFormSubmit &&
        onFormSubmit(getSubmitInputs(inputs));

      return inputs;
    });
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    const files = event.target.files ? Array.from(event.target.files) : [];

    setInputs((prevState) =>
      prevState.map((prevInput) =>
        prevInput.name === event.target.name
          ? {
              ...prevInput,
              value:
                prevInput.multiple && Array.isArray(prevInput.value)
                  ? [...prevInput.value, ...files]
                  : files[0],
              isValidatable: true,
              validationErrors: files.length
                ? getValidationError(
                    prevInput.multiple ? files : files[0],
                    prevInput.validation,
                    intl,
                  )
                : [],
            }
          : { ...prevInput },
      ),
    );
  };

  const onFileDropped = (name: string, file: File) => {
    setInputs((prevState) => {
      const inputs = prevState.map((prevInput) =>
        prevInput.name === name
          ? {
              ...prevInput,
              value: file,
              isValidatable: true,
              validationErrors: file
                ? getValidationError(file, prevInput.validation, intl)
                : [],
            }
          : { ...prevInput },
      );

      !inputs.some((input) => input.validationErrors?.length) &&
        formBehavior?.submitOnChange &&
        onFormSubmit &&
        onFormSubmit(getSubmitInputs(inputs));

      return inputs;
    });
  };

  const onSelectChange = (
    value: string | string[] | any[],
    name: string,
    freeSolo?: boolean,
  ) => {
    setInputs((prevState) => {
      const inputs = prevState.map((prevInput) => {
        const getOptions = () => {
          const optionLength = value.length - 1;

          const optionExists = prevInput.options?.find(
            (option) => option.value === value[optionLength],
          );

          if (optionExists) {
            return prevInput?.options ? [...prevInput.options] : undefined;
          }

          if (freeSolo) {
            return prevInput.options
              ? [
                  ...prevInput.options,
                  { value: value[optionLength], label: value[optionLength] },
                ]
              : undefined;
          }

          return prevInput.options ? [...prevInput.options] : undefined;
        };

        return prevInput.name === name
          ? {
              ...prevInput,
              value: value,
              options: getOptions(),
              validationErrors: prevInput.isValidatable
                ? getValidationError(value, prevInput.validation, intl)
                : [],
            }
          : { ...prevInput };
      });

      formBehavior?.submitOnChange &&
        onFormSubmit &&
        onFormSubmit(getSubmitInputs(inputs));

      return inputs;
    });
  };

  const onDynamicChange = (
    value: Record<string, any>[],
    dynamicConfig: FormInput[],
    name: string,
  ) => {
    setInputs((prevState) => {
      const inputs = prevState.map((prevInput) => {
        return prevInput.name === name
          ? {
              ...prevInput,
              value,
              dynamicInputValidationErrors: value.map((group) =>
                Object.keys(group).reduce<Record<string, any>>((acc, field) => {
                  const rules =
                    dynamicConfig.find((config) => config.name === field)
                      ?.validation || [];
                  const value = group[field];

                  acc[field] = getValidationError(value, rules, intl);

                  return acc;
                }, {}),
              ),
            }
          : { ...prevInput };
      });

      formBehavior?.submitOnChange &&
        onFormSubmit &&
        onFormSubmit(getSubmitInputs(inputs));

      return inputs;
    });
  };

  const onClearInput = (name: string) => {
    setInputs((prevState) =>
      prevState.map((prevInput) =>
        prevInput.name === name
          ? {
              ...prevInput,
              value: '',
              validationErrors: prevInput.isValidatable
                ? getValidationError('', prevInput.validation, intl)
                : [],
            }
          : { ...prevInput },
      ),
    );
  };

  const onLoseInputFocus = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    setInputs((prevState) =>
      prevState.map((prevInput) =>
        prevInput.name === event.target.name
          ? {
              ...prevInput,
              isValidatable: true,
              validationErrors: getValidationError(
                prevInput.value instanceof File
                  ? ''
                  : (IGNORED_TRIM.includes(prevInput.type)
                      ? prevInput.value
                      : trim(prevInput.value?.toString())) || '',
                prevInput.validation,
                intl,
              ),
            }
          : { ...prevInput },
      ),
    );

    formBehavior?.submitOnBlur &&
      onFormSubmit &&
      onFormSubmit(getSubmitInputs(inputs));
  };

  const onInputBlur = (name: string) => {
    setInputs((prevState) =>
      prevState.map((prevInput) =>
        prevInput.name === name
          ? {
              ...prevInput,
              isValidatable: true,
              validationErrors:
                prevInput.type === 'translations'
                  ? onTranslationValidation(prevInput)
                  : getValidationError(
                      (IGNORED_TRIM.includes(prevInput.type)
                        ? prevInput.value
                        : trim(prevInput.value?.toString())) || '',
                      prevInput.validation,
                      intl,
                    ),
            }
          : { ...prevInput },
      ),
    );
  };

  const setNewInputObject = (name: string, newValue: any) => {
    setInputs((prevState) =>
      prevState.map((prevInput) =>
        prevInput.name === name
          ? {
              ...prevInput,
              ...newValue,
            }
          : { ...prevInput },
      ),
    );
  };

  const setInputValue = (
    name: string,
    newValue: string | string[] | number | File | File[] | any[],
  ) => {
    setInputs((prevState) =>
      prevState.map((prevInput) =>
        prevInput.name === name
          ? {
              ...prevInput,
              value: newValue,
              validationErrors: prevInput.isValidatable
                ? getValidationError(newValue, prevInput.validation, intl)
                : [],
            }
          : { ...prevInput },
      ),
    );
  };

  const setNewInputs = (newInputs: Array<FormInput>) => {
    setInputs(newInputs);
  };

  const onSubmit = (event?: ChangeEvent) => {
    event?.preventDefault();

    setInputs(
      inputs.map((input) => ({
        ...input,
        validationErrors: [],
      })),
    );

    const validatedInputs = inputs.map((input) => ({
      ...input,
      validationErrors:
        input.type === 'translations'
          ? onTranslationValidation(input)
          : getValidationError(
              input.value instanceof File
                ? input.value
                : (IGNORED_TRIM.includes(input.type)
                    ? input.value
                    : trim(input.value?.toString())) || '',
              input.validation,
              intl,
            ),
      dynamicInputValidationErrors:
        input.value && input.type === 'dynamic'
          ? (input.value as Record<string, any>[]).map((group) =>
              Object.keys(group).reduce<Record<string, any>>((acc, field) => {
                const rules =
                  input.dynamicFieldConfig?.find(
                    (config) => config.name === field,
                  )?.validation || [];
                const value = group[field];

                acc[field] = getValidationError(value, rules, intl);

                return acc;
              }, {}),
            )
          : [],
    }));

    if (
      _.some(
        validatedInputs,
        (validatedInput) =>
          !_.isEmpty(validatedInput.validationErrors) ||
          _.some(
            validatedInput.dynamicInputValidationErrors,
            (dynamicErrors: Record<string, string[]>) =>
              _.some(
                Object.values(dynamicErrors),
                (errors) => !_.isEmpty(errors),
              ),
          ),
      )
    ) {
      setInputs(validatedInputs);
      return;
    }

    handleSubmit();
  };

  const handleSubmit = () => {
    onFormSubmit &&
      onFormSubmit(
        Object.assign(
          {},
          ...inputs.map((input) => ({
            [input.name]: IGNORED_TRIM.includes(input.type)
              ? input.value
              : trim(input.value?.toString()),
          })),
        ),
      );
  };

  const onFileDelete = (name: string, file: string | File) => {
    setInputs((prevState) =>
      prevState.map((prevInput) =>
        prevInput.name === name
          ? {
              ...prevInput,
              value:
                prevInput.multiple && Array.isArray(prevInput.value)
                  ? prevInput.value.filter((val: string | File) => {
                      if (file instanceof File && val instanceof File) {
                        return file.name !== val.name;
                      }

                      return val !== file;
                    })
                  : '',
              validationErrors: prevInput.isValidatable
                ? getValidationError('', prevInput.validation, intl)
                : [],
            }
          : { ...prevInput },
      ),
    );
  };

  const getSubmitInputs = (submitInputs: Array<FormInput>) =>
    Object.assign(
      {},
      ...submitInputs.map((input) => ({
        [input.name]:
          IGNORED_TRIM.includes(input.type) || Array.isArray(input.value)
            ? input.value
            : trim(input.value?.toString()),
      })),
    );

  const onSetValidationErrors = (error: HttpError) => {
    if (_.isArray(error)) {
      error.forEach((fieldError) => {
        setInputs((prevState) =>
          prevState.map((prevInput) =>
            prevInput.name === fieldError.field ||
            prevInput.alternativeNames?.includes(fieldError.field)
              ? {
                  ...prevInput,
                  isValidatable: true,
                  validationErrors: [fieldError.message],
                }
              : { ...prevInput },
          ),
        );
      });
    }
  };

  const onTranslationValidation = (translationInput: FormInput): string[] => {
    if (!translationInput) {
      return [];
    }

    let errors: string[] = [];

    for (const translation of translationInput.value as EntityTranslation[]) {
      const defaultInput = inputs.filter(
        (input) => input.name === translation.property,
      )[0];

      if (!defaultInput) {
        continue;
      }

      if (translationInput.areTranslationsOptional) {
        defaultInput.validation = defaultInput.validation?.filter(
          (singleValidation) => singleValidation.type !== 'required',
        );
      }

      const inputErrors = getValidationError(
        translation.value,
        defaultInput.validation,
        intl,
      ).map(
        (error) => `${translation.property}_${translation.languageId}:${error}`,
      );

      errors = [...errors, ...inputErrors];
    }

    return errors;
  };

  return {
    inputs,
    onInputChange,
    onCheckboxChange,
    onTimeChange,
    onFileChange,
    onLoseInputFocus,
    onSubmit,
    onClearInput,
    setNewInputObject,
    onSelectChange,
    onFileDelete,
    getSubmitInputs,
    onSetValidationErrors,
    onInputBlur,
    onInputValueChange,
    onTranslationsChange,
    onFileDropped,
    onRadioChange,
    onNumberInputChange,
    setNewInputs,
    setInputValue,
    onDynamicChange,
  };
};
